import {
  FOC_TOUR_LABEL_ORG_WORKSPACE,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';

/** Opens Hub AORs and starts the organizational workspace walkthrough. */
export const ORG_WORKSPACE_TOUR_HREF = '/hub?openTab=AORs&orgWorkspaceTour=1';

export const ORG_WORKSPACE_TOUR_PARAM = 'orgWorkspaceTour';

export type OrgWorkspaceTourStepId =
  | 'intro'
  | 'structure'
  | 'filterArea'
  | 'filterAreaSitStat'
  | 'expandDistrict'
  | 'expandSector'
  | 'navigate'
  | 'areaDetails'
  | 'districtDetails'
  | 'sectorDetails'
  | 'subunitDetails'
  | 'areaOnMap'
  | 'zoomOnMap'
  | 'allAreasOnMap'
  | 'orgEntry'
  | 'orgContext'
  | 'orgDrillDown'
  | 'orgData'
  | 'orgGeospatial'
  | 'orgOwnership'
  | 'orgSymbology'
  | 'orgHomeScope'
  | 'unitAwareness'
  | 'workspaceToIncident'
  | 'focFeedback';

export const IOC_CARRYOVER_DISPOSITION = 'IOC Carryover / FOC Required';
export const FOC_REQUIRED_DISPOSITION = 'FOC Required';

/** Where a step's target lives: the AORs tab, or the AOR View SitStat. */
export type OrgWorkspaceTourSurface = 'aors' | 'sitstat';

export type OrgWorkspaceTourStepDef = {
  id: OrgWorkspaceTourStepId;
  /** Spec requirement id; the section intro has none. */
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  /** Optional bullet list rendered under the description in the popover. */
  bullets?: string[];
  surface: OrgWorkspaceTourSurface;
  /** `data-tour` value. Missing targets become a centered popover. */
  tour?: string;
};

type RequirementInput = Omit<
  OrgWorkspaceTourStepDef,
  'title' | 'surface' | 'requirementId'
> & {
  requirementId: string;
  name: string;
  surface?: OrgWorkspaceTourSurface;
};

function requirement({
  requirementId,
  name,
  surface = 'aors',
  ...rest
}: RequirementInput): OrgWorkspaceTourStepDef {
  return {
    ...rest,
    requirementId,
    surface,
    title: `${requirementId} — ${name}`,
  };
}

export const ORG_WORKSPACE_TOUR_STEPS: OrgWorkspaceTourStepDef[] = [
  {
    id: 'intro',
    surface: 'aors',
    tour: 'aor-workspace',
    title: '4.1 Organizational Workspace Model',
    description:
      'PRATUS provides Coast Guard organizational workspaces that support awareness, planning, incident management, exercises, resource visibility, and applicable data roll-up at the appropriate organizational echelon. Organizational workspaces include, as applicable: Sector; District; Area; Headquarters; National Strike Force; subordinate units; stations; bases; air stations; cutters; operational facilities; other units identified in authoritative organizational data; and future authorized organizational structures.',
  },
  requirement({
    id: 'structure',
    requirementId: 'US-RG002',
    name: 'View Organizational Structure',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-hierarchy',
    description:
      'View Organizational Structure: As a user, I want to see the hierarchical structure of Areas, Districts, Sectors, Subunits, and Resources (5 levels) so that I understand organizational boundaries and relationships. This list is Areas, Districts, Sectors, and Subunits. Resources, the fifth level, are tracked on the Resources tab.',
  }),
  requirement({
    id: 'filterArea',
    requirementId: 'US-RG003',
    name: 'Filter by Area',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-area-filters',
    description:
      'Filter by Area: As a user, I want to filter the view by selecting a specific Area (e.g., Atlantic Area), so that I can focus on the districts and units within that top-level jurisdiction.',
  }),
  requirement({
    id: 'filterAreaSitStat',
    requirementId: 'US-RG003',
    name: 'Filter by Area (AOR View SitStat)',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'sitstat',
    tour: 'sitstat-aor-filter-bar',
    description:
      'View SitStat on any AOR opens the AOR View SitStat filtered to that AOR and everything beneath it. Notifications, events, incidents, AORs, map layers, tasks, assets, and the map polygons all follow the AOR filter, and it can be switched to any Area, District, Sector, or SubUnit here.',
  }),
  requirement({
    id: 'expandDistrict',
    requirementId: 'US-RG004',
    name: 'Expand District to View Sectors',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-district-card',
    description:
      'Expand District to View Sectors: As a user, I want to expand a district to see its sectors, so that I can drill down to specific areas of responsibility.',
  }),
  requirement({
    id: 'expandSector',
    requirementId: 'US-RG005',
    name: 'Expand Sector to View SubUnits',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-sector-subunits',
    description:
      'Expand Sector to View SubUnits: As a user, I want to expand a sector to see its individual units (e.g., Stations, Bases), so that I can access the most granular operational level.',
  }),
  requirement({
    id: 'navigate',
    requirementId: 'US-RG007',
    name: 'Navigate Multi-Level Hierarchy',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-hierarchy-chips',
    description:
      'Navigate Multi-Level Hierarchy: As a user, I want to easily understand which district, sector, subunit, or resource belongs to which parent, so that I maintain geographic context.',
  }),
  requirement({
    id: 'areaDetails',
    requirementId: 'US-RG008',
    name: 'View Area Details',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-area-selected',
    description:
      'View Area Details: As a user, I want to see detailed information about an Area (the top-level jurisdiction), so that I understand its scope and characteristics.',
  }),
  requirement({
    id: 'districtDetails',
    requirementId: 'US-RG009',
    name: 'View District Details',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-district-details',
    description:
      'View District Details: As a user, I want to see detailed information about a district, so that I understand its jurisdiction.',
  }),
  requirement({
    id: 'sectorDetails',
    requirementId: 'US-RG010',
    name: 'View Sector Details',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-sector-details',
    description:
      'View Sector Details: As a user, I want to see detailed information about a sector, so that I understand operational boundaries.',
  }),
  requirement({
    id: 'subunitDetails',
    requirementId: 'US-RG011',
    name: 'View SubUnit Details',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-subunit-details',
    description:
      'View SubUnit Details: As a user, I want to see detailed information about a specific subunit, so that I can access contact info and operational data.',
  }),
  requirement({
    id: 'areaOnMap',
    requirementId: 'US-RG012',
    name: 'View Area on Map',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-map-button',
    description:
      "View Area on Map: As a user, I want to click a map icon to see an Area's geographic boundaries on the map, so that I understand its coverage.",
  }),
  requirement({
    id: 'zoomOnMap',
    requirementId: 'US-RG013',
    name: 'Zoom to District, Sector, or Unit on Map',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-map-view',
    description:
      'Zoom to District, Sector, or Unit on Map: As a user, I want to zoom to specific districts, sectors, or units on the map, so that I can see detailed geography.',
  }),
  requirement({
    id: 'allAreasOnMap',
    requirementId: 'US-RG014',
    name: 'View Multiple Areas on Map Simultaneously',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'aor-map-all',
    description:
      'View Multiple Areas on Map Simultaneously: As a user, I want to see all Area boundaries on a single map, so that I can understand geographic relationships.',
  }),
  requirement({
    id: 'orgEntry',
    requirementId: 'FOC-ORG-001',
    name: 'Organizational Orientation at Entry',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'aor-workspace',
    description:
      "PRATUS provides users a landing and Hub experience that makes the user's Coast Guard organizational context and Area of Responsibility discoverable without requiring platform-specific navigation knowledge.",
  }),
  requirement({
    id: 'orgContext',
    requirementId: 'FOC-ORG-001a',
    name: 'Geographic and Organizational Context',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'aor-map-view',
    description:
      'The landing experience presents recognizable Area, District, Sector, and applicable subordinate-unit context through the Hub/COP, organizational controls, or both.',
  }),
  requirement({
    id: 'orgDrillDown',
    requirementId: 'FOC-ORG-001b',
    name: 'Drill-Down Behavior',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'aor-hierarchy-chips',
    description:
      'Users can move from Area to District, District to Sector, Sector to subordinate unit, and unit to relevant operational or incident information using visible geographic and organizational navigation.',
  }),
  requirement({
    id: 'orgData',
    requirementId: 'FOC-ORG-001c',
    name: 'Organizational Data Dependency',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'PRATUS uses authoritative Coast Guard organizational and OPFAC-related data when available and approved. The authoritative source, ownership, refresh method, and interface approach are documented in Appendix A or as a Government Decision Required.',
  }),
  requirement({
    id: 'orgGeospatial',
    requirementId: 'FOC-ORG-001d',
    name: 'Authoritative Unit Geospatial Record',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'aor-subunit-details',
    description:
      'PRATUS maintains or consumes an approved locations record for fixed Coast Guard units/facilities, including items such as unit identity, type, OPFAC/identifier, parent organization/command, address, lat/long, status, source, and last update. Mobile assets are handled separately from fixed unit points.',
  }),
  requirement({
    id: 'orgOwnership',
    requirementId: 'FOC-ORG-001e',
    name: 'Organizational Ownership and Command Relationship',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'aor-opcon-assets',
    description:
      'PRATUS preserves authoritative parent/child, ownership, and OPCON/TACON relationships. Geographic proximity alone does not change ownership. Example: A boat remains associated with its Station and an aircraft remains associated with its Air Station unless an approved reassignment of that resource changes that relationship. Contextually, the boat/aircraft/person could support another AOR or incident, but they are always grounded with their home unit.',
    bullets: [
      "Use View Assets on a unit to see seeded resources under that unit's OPCON.",
      'Each asset shows an ALMIS origin badge; home-unit association is preserved even when the asset supports this AOR.',
    ],
  }),
  requirement({
    id: 'orgSymbology',
    requirementId: 'FOC-ORG-001f',
    name: 'Unit Map Identity and Symbology',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'aor-map-view',
    description:
      'Unit locations use consistent Coast Guard recognizable symbols. Approved seals/logos should be used for HQ, AREAs, Districts, Air Stations, Sectors, Deployable Specialized Forces, and the National Strike Force. When official seals/logos are not available, PRATUS will use an approved standard unit symbol and will not create unofficial branding.',
  }),
  requirement({
    id: 'orgHomeScope',
    requirementId: 'FOC-ORG-001g',
    name: 'Home-Organization Default Scope',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'aor-workspace',
    description:
      "PRATUS shall use the user's authoritative organizational affiliation, permissions, and active organizational context to establish the initial Hub/workspace scope. The default view shall:",
    bullets: [
      "Prioritize the user's organization, subordinate units, and Area of Responsibility",
      'Avoid requiring manual navigation from a National view before locally relevant information is visible',
      'Preserve the ability to intentionally navigate to higher, lower, or adjacent organizational views',
      'Support users whose duties require access across multiple organizational contexts (e.g., Deployable Specialized Forces, National Strike Force, Logistics Command, Communications Command, Exercise Support Team, etc.)',
    ],
  }),
  requirement({
    id: 'unitAwareness',
    requirementId: 'FOC-ORG-002',
    name: 'Unit Workspace Awareness',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'sitstat',
    tour: 'sitstat-workspace',
    description:
      'Unit workspaces provide authorized users a role-appropriate view of active incidents, exercises, organizational readiness, relevant alerts, applicable operational status, and approved links to related incident workspaces.',
  }),
  requirement({
    id: 'workspaceToIncident',
    requirementId: 'FOC-ORG-003',
    name: 'Workspace-to-Incident Connection',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'sitstat',
    tour: 'sitstat-widget-incidents',
    description:
      'PRATUS enables authorized users to move between unit-level awareness and the relevant incident workspace when an incident begins, escalates, transfers, or requires sustained incident-management activity.',
  }),
  {
    id: 'focFeedback',
    surface: 'sitstat',
    title: 'FOC Feedback',
    // Prebuilt HTML with a clickable link; toDriveStep must not escape it.
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_ORG_WORKSPACE),
  },
];

/**
 * Product Tours nav lists this tour only when the USCG FOC Requirements flag is
 * on.
 */
export function orgWorkspaceTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function orgWorkspaceTourStepIds(): OrgWorkspaceTourStepId[] {
  return ORG_WORKSPACE_TOUR_STEPS.map((step) => step.id);
}

export function orgWorkspaceTourStepTitles(): string[] {
  return ORG_WORKSPACE_TOUR_STEPS.map((step) => step.title);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Popover body: the disposition line first, then the requirement text. */
export function orgWorkspaceTourPopoverHtml(
  step: Pick<OrgWorkspaceTourStepDef, 'disposition' | 'description' | 'bullets'>
): string {
  const body = `<p>${escapeHtml(step.description)}</p>`;
  const bullets = step.bullets?.length
    ? `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    : '';
  const content = `${body}${bullets}`;
  if (!step.disposition) return content;
  return `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>${content}`;
}
